import {
  Activity,
  Client,
  Enrollment,
  EnrollmentPaymentStatus,
  EnrollmentRequestV3,
  Payment,
  PaymentMethod,
  PaymentSource,
  PriceDetails
} from '../types/GrooverTypes'
import { calculateDiscounts, getPriceFromCalculation } from './calculateLogic/CalculateDiscountHelpers'
import { Calculation } from './calculateLogic/CalculateDiscountTypes'
import { RefundableSwishPayment, RefundableVippsPayment } from '../types/SwishTypes'
import { RefundableStripePayment } from '../types/StripeTypes'

export const createPriceDetailsFromEnrollmentRequestV3 = ({
  request,
  activities,
  client
}: {
  request: EnrollmentRequestV3
  activities: Activity[]
  client: Client
}): PriceDetails[] => {
  const paymentMethod: PaymentMethod =
    request.paymentInput.find((input) => input.user === 'mainUser')?.method || PaymentMethod.BankTransfer

  const mainUserDiscounts: Calculation[] = calculateDiscounts(activities, client, {
    isStudent: request.discountUserInput.mainUser.isStudent,
    isSibling: request.discountUserInput.mainUser.isSibling,
    isSenior: request.discountUserInput.mainUser.isSenior,
    userActivePeriods: request.discountUserInput.mainUser.userActivePeriods || [],
    userEnrolledActivityIds: request.discountUserInput.mainUser.userEnrolledActivityIds,
    userEnrolledPeriodIds: request.discountUserInput.mainUser.userEnrolledPeriodIds,
    userEnrolledActivityPeriods: request.discountUserInput.mainUser.userEnrolledActivityPeriods,
    usedContinuationDiscountIds: request.discountUserInput.mainUser.usedContinuationDiscountIds,
    usedContinuationDiscountActivities: request.discountUserInput.mainUser.usedContinuationDiscountActivities,
    clientPeriods: client.periods,
    participantsAmount: request.discountUserInput.participantsAmount,
    registeredPartners: request.discountUserInput.registeredPartners, // TODO: should be empty?
    discountCode: request.discountUserInput.discountCode,
    priceOverride: request.discountUserInput.priceOverride,
    activityQuantities: request.activityQuantities
  })
  let partnerUserDiscounts: Calculation[] = []
  if (request.partnerUser) {
    partnerUserDiscounts = calculateDiscounts(activities, client, {
      isStudent: request.discountUserInput.partnerUser?.isStudent || false,
      isSibling: request.discountUserInput.partnerUser?.isSibling || false,
      isSenior: request.discountUserInput.partnerUser?.isSenior || false,
      userActivePeriods: request.discountUserInput.partnerUser?.userActivePeriods || [],
      userEnrolledActivityIds: request.discountUserInput.partnerUser?.userEnrolledActivityIds,
      userEnrolledPeriodIds: request.discountUserInput.partnerUser?.userEnrolledPeriodIds,
      userEnrolledActivityPeriods: request.discountUserInput.partnerUser?.userEnrolledActivityPeriods,
      usedContinuationDiscountIds: request.discountUserInput.partnerUser?.usedContinuationDiscountIds,
      usedContinuationDiscountActivities: request.discountUserInput.partnerUser?.usedContinuationDiscountActivities,
      clientPeriods: client.periods,
      participantsAmount: request.discountUserInput.participantsAmount, // TODO: participants amount should be read from registeredPartners
      registeredPartners: request.discountUserInput.registeredPartners, // this is where we know if there is any partner
      discountCode: request.discountUserInput.discountCode, // partner can use discount code too
      activityQuantities: request.activityQuantities
    })
  }

  const totalFinalPriceOre =
    mainUserDiscounts.reduce((sum, c) => sum + getPriceFromCalculation(c), 0) +
    partnerUserDiscounts.reduce((sum, c) => sum + getPriceFromCalculation(c), 0)

  // Admin fee is per transaction, not per price detail — only when there is something to pay
  const administrationFee = resolveAdministrationFeeAmount(paymentMethod, client, totalFinalPriceOre)

  // Create price details with admin fee set only on the first main user price detail
  const mainUserPriceDetails = mainUserDiscounts.map((calculation, index) => {
    const pd = createPriceDetailsFromCalculation(calculation, paymentMethod, 'mainUser')
    // Set admin fee only on the first price detail (per transaction)
    if (index === 0) {
      pd.administrationFee = administrationFee
    }
    return pd
  })

  // Partner user price details: admin fee should be 0 (already set on first main user price detail)
  const partnerUserPriceDetails = partnerUserDiscounts.map((calculation) =>
    createPriceDetailsFromCalculation(calculation, paymentMethod, 'partnerUser')
  )

  return [...mainUserPriceDetails, ...partnerUserPriceDetails]
}

export const createPriceDetailsFromCalculation = (
  calculation: Calculation,
  paymentMethod: PaymentMethod,
  appliesTo: string
): PriceDetails => {
  const priceWithDiscounts = getPriceFromCalculation(calculation)
  const pd: PriceDetails = {
    activityId: calculation.activity.id,
    originalPrice: calculation.activity.price,
    finalPrice: priceWithDiscounts,
    totalDiscountPrice: calculation.activity.price - priceWithDiscounts,
    paymentMethod,
    administrationFee: 0,
    discountIds: [
      ...new Set(calculation.discounts.filter((discount) => discount.discount > 0).map((discount) => discount.id))
    ],
    appliesTo
  }
  return pd
}

export const getPaymentMethodFee = (paymentMethod: PaymentMethod, client: Client): number => {
  return client.paymentMethodFees?.find((fee) => fee.method === paymentMethod)?.fee || 0
}

export const isPaymentMethodAdministrationFeeEnabled = (client: Client): boolean => {
  return client.featureToggles?.paymentMethodAdministrationFee === true
}

/** Applies toggle + free-total guards before returning a fee to charge or store on an enrollment. */
export function resolveAdministrationFeeAmount(
  paymentMethod: PaymentMethod,
  client: Client,
  totalFinalPriceOre: number
): number {
  if (totalFinalPriceOre <= 0) return 0
  if (!isPaymentMethodAdministrationFeeEnabled(client)) return 0
  return getPaymentMethodFee(paymentMethod, client)
}

/** Whether stored priceDetails administrationFee values should count toward due/unpaid totals. */
export function shouldIncludeStoredAdministrationFee(sumOfFinalPrices: number, client?: Client): boolean {
  if (sumOfFinalPrices <= 0) return false
  if (client && !isPaymentMethodAdministrationFeeEnabled(client)) return false
  return true
}

export const isPaymentMethodEnabled = (paymentMethod: PaymentMethod, client: Client): boolean => {
  return client.paymentMethodFees?.some((fee) => fee.method === paymentMethod) ?? false
}

/**
 * Government subsidy cap for Fritidskortet (in öre).
 * Fritidskortet covers up to this amount per enrollment; the family pays the rest.
 * TODO: Consider making this configurable per client if subsidy amounts vary by region.
 */
export const FRITIDSKORTET_SUBSIDY_AMOUNT_ORE = 55000

/**
 * Given the total activity amount (including any payment method fee), returns
 * how much Fritidskortet covers and how much the family still pays.
 */
export function calcFritidskortetAmounts(totalInOre: number): {
  fritidskortetCoversInOre: number
  userPaysInOre: number
} {
  const fritidskortetCoversInOre = Math.min(totalInOre, FRITIDSKORTET_SUBSIDY_AMOUNT_ORE)
  const userPaysInOre = Math.max(0, totalInOre - FRITIDSKORTET_SUBSIDY_AMOUNT_ORE)
  return { fritidskortetCoversInOre, userPaysInOre }
}

export const getTotalPriceForEnrollment = (
  enrollment: Pick<Enrollment, 'priceDetails'>,
  appliesTo?: 'mainUser' | 'partnerUser'
): number => {
  const filteredDetails = enrollment.priceDetails?.filter((pd) => !appliesTo || pd.appliesTo === appliesTo) || []

  return filteredDetails.reduce((acc, curr) => acc + curr.finalPrice + curr.administrationFee, 0)
}

export const getPaidAmountForEnrollment = (enrollment: Enrollment): number =>
  enrollment.payments?.reduce((total, payment) => {
    return total + payment.totalAmount
  }, 0) || 0

/**
 * Appends one or more `pay` rows to `enrollment.payments` for Fritidskortet / ePassi-style flows.
 * `totalAmount` is in the smallest currency unit (e.g. öre, cents) consistent with the rest of enrollment payments.
 * Initializes `enrollment.payments` if missing.
 */
export function appendEnrollmentPaymentsForPayFor(args: {
  enrollment: Enrollment
  currency: string
  totalAmount: number
  paymentRef: string
  paymentMethod: PaymentMethod
  source: PaymentSource
  payFor: 'mainUser' | 'partnerUser' | 'both'
  paymentDate: string
}): void {
  const { enrollment, currency, totalAmount, paymentRef, paymentMethod, source, payFor, paymentDate } = args
  if (!enrollment.payments) {
    enrollment.payments = []
  }
  const mainId = enrollment.mainUser?.id
  const partnerId = enrollment.partnerUser?.id

  const row = (overrides: Partial<Payment> & Pick<Payment, 'totalAmount'>): void => {
    enrollment.payments!.push({
      currency,
      paymentDate,
      paymentRef,
      paymentMethod,
      type: 'pay',
      source,
      ...overrides
    })
  }

  if (payFor === 'mainUser' && mainId) {
    row({ totalAmount, userId: mainId })
  } else if (payFor === 'partnerUser' && partnerId) {
    row({ totalAmount, userId: partnerId })
  } else if (payFor === 'both' && mainId) {
    if (partnerId) {
      const half = Math.floor(totalAmount / 2)
      const remainder = totalAmount - half
      row({ totalAmount: half, userId: mainId })
      if (remainder > 0) {
        row({ totalAmount: remainder, userId: partnerId })
      }
    } else {
      row({ totalAmount, userId: mainId })
    }
  } else {
    row({ totalAmount })
  }
}

export const getEnrollmentPaymentStatus = (enrollment: Enrollment): EnrollmentPaymentStatus => {
  const totalCost = getTotalPriceForEnrollment(enrollment)
  const payments = enrollment.payments || []
  const totalPaid = payments.filter((p) => p.type === 'pay').reduce((s, p) => s + p.totalAmount, 0)
  const totalRefunded = payments.filter((p) => p.type === 'refund').reduce((s, p) => s + Math.abs(p.totalAmount), 0)
  const netPaid = totalPaid - totalRefunded

  if (totalCost === 0) return EnrollmentPaymentStatus.Paid

  if (totalRefunded > 0) {
    if (netPaid <= 0) return EnrollmentPaymentStatus.Refunded
    if (netPaid < totalCost) return EnrollmentPaymentStatus.PartlyRefunded
  }

  if (netPaid >= totalCost) return EnrollmentPaymentStatus.Paid
  if (netPaid > 0) return EnrollmentPaymentStatus.PartlyPaid
  return EnrollmentPaymentStatus.Unpaid
}

export const formatPrice = (price: number): string => {
  return (price / 100).toFixed(0)
}

/**
 * Returns true if the enrollment already has a Fritidskortet `pay` entry for the given
 * payment session ID. Used to make `fritidskortetConfirmPayout` idempotent.
 */
export function enrollmentHasFritidskortetPaymentForSession(enrollment: Enrollment, paymentSessionId: string): boolean {
  return (enrollment.payments ?? []).some(
    (p) => p.type === 'pay' && p.source === PaymentSource.Fritidskortet && p.paymentRef === paymentSessionId
  )
}

export const findRefundableSwishPayments = (enrollment: Enrollment): RefundableSwishPayment[] => {
  // Get all Swish payments (positive amounts) and their corresponding refunds (negative amounts)
  const isSwishSource = (source: string | undefined) =>
    source === PaymentSource.Swish || source === PaymentSource.SwishWalkIn

  const swishPayments =
    enrollment.payments?.filter(
      (p) => p.paymentMethod === PaymentMethod.Swish && p.type === 'pay' && isSwishSource(p.source)
    ) || []

  const swishRefunds =
    enrollment.payments?.filter(
      (p) => p.paymentMethod === PaymentMethod.Swish && p.type === 'refund' && isSwishSource(p.source)
    ) || []

  // Map each payment to its refundable status
  return swishPayments
    .map((payment) => {
      // Find all refunds for this payment using originalPaymentReference
      const paymentRefunds = swishRefunds.filter(
        (refund) => refund.originalPaymentReference === payment.swishPaymentReference
      )

      // Calculate total amount already refunded
      const alreadyRefunded = Math.abs(paymentRefunds.reduce((sum, refund) => sum + refund.totalAmount, 0))

      // Calculate remaining amount that can be refunded
      const originalAmount = payment.totalAmount
      const remainingAmount = originalAmount - alreadyRefunded

      return {
        instructionUUID: payment.paymentRef!,
        amount: remainingAmount,
        originalAmount,
        alreadyRefunded
      }
    })
    .filter((payment) => payment.amount > 0) // Only include payments that still have amount to refund
}

export const findRefundableVippsPayments = (enrollment: Enrollment): RefundableVippsPayment[] => {
  // Get all Vipps payments (positive amounts) and their corresponding refunds (negative amounts)
  const payments =
    enrollment.payments?.filter(
      (p) => p.paymentMethod === PaymentMethod.VippsMobile && p.type === 'pay' && p.source === PaymentSource.Vipps
    ) || []

  const refunds =
    enrollment.payments?.filter(
      (p) => p.paymentMethod === PaymentMethod.VippsMobile && p.type === 'refund' && p.source === PaymentSource.Vipps
    ) || []

  // Map each payment to its refundable status
  return payments
    .map((payment) => {
      // Find all refunds for this payment (payments and refunds will have the same reference - pointing to the original payment)
      const paymentRefunds = refunds.filter((refund) => refund.paymentRef === payment.paymentRef)

      // Calculate total amount already refunded
      const alreadyRefunded = Math.abs(paymentRefunds.reduce((sum, refund) => sum + refund.totalAmount, 0))

      // Calculate remaining amount that can be refunded
      const originalAmount = payment.totalAmount
      const remainingAmount = originalAmount - alreadyRefunded

      return {
        reference: payment.paymentRef!,
        amount: remainingAmount,
        originalAmount,
        alreadyRefunded
      }
    })
    .filter((payment) => payment.amount > 0) // Only include payments that still have amount to refund
}

/**
 * Stripe `pay` entries store the raw Stripe payment-method id (e.g. `pm_xxx`) in
 * `payment.paymentMethod`, not the PaymentMethod.Stripe enum value (see addStripePaymentForEnrollment),
 * so unlike Swish/Vipps this filters by source only. Includes `StripeWalkIn` (Stripe Terminal reception
 * card walk-ins) and `StripeTerminal` (Stripe Terminal Trigger Pay against an existing enrollment,
 * COU-325) alongside online `Stripe` — all three settle through the same refund path (req. 20-21).
 * Refund rows are always tagged `PaymentSource.Stripe` regardless of the original payment's source
 * (see `buildStripeRefundPaymentRow`), so the refund-side filter does not need `StripeWalkIn` /
 * `StripeTerminal`.
 */
/** True when Swish, Stripe, or Vipps provider refunds are still available via API. */
export const hasProviderRefundablePayments = (enrollment: Enrollment): boolean =>
  findRefundableSwishPayments(enrollment).length > 0 ||
  findRefundableStripePayments(enrollment).length > 0 ||
  findRefundableVippsPayments(enrollment).length > 0

export const findRefundableStripePayments = (enrollment: Enrollment): RefundableStripePayment[] => {
  const isStripeSource = (source: string | undefined) =>
    source === PaymentSource.Stripe || source === PaymentSource.StripeWalkIn || source === PaymentSource.StripeTerminal
  const stripePayments = enrollment.payments?.filter((p) => p.type === 'pay' && isStripeSource(p.source)) || []
  const stripeRefunds =
    enrollment.payments?.filter((p) => p.type === 'refund' && p.source === PaymentSource.Stripe) || []

  return stripePayments
    .map((payment) => {
      const refundsForThisPayment = stripeRefunds.filter(
        (refund) => refund.originalPaymentReference === payment.paymentRef
      )
      const alreadyRefunded = Math.abs(refundsForThisPayment.reduce((sum, r) => sum + r.totalAmount, 0))
      const originalAmount = payment.totalAmount
      const remainingAmount = originalAmount - alreadyRefunded
      return {
        paymentIntentId: payment.paymentRef!,
        amount: remainingAmount,
        originalAmount,
        alreadyRefunded,
        paymentInfo: payment.paymentInfo,
        userId: payment.userId
      }
    })
    .filter((p) => p.amount > 0)
}

/**
 * Sources settled by an external payment gateway (Swish, Stripe, Vipps, Fritidskortet, Epassi —
 * including their walk-in / terminal variants). A payment on one of these sources was captured
 * by the gateway, not typed in by an operator, so deleting it from the enrollment would silently
 * desync Firestore from the gateway's own record — it must be refunded through the gateway's
 * refund flow instead, never deleted.
 *
 * This is the source list only. Delete guards in `enrollmentActions.ts`,
 * `EnrollmentDetailsPage.tsx` and `EnrollmentPaymentsSection.tsx` must use
 * `canDeletePayment` — do not re-hand-roll this list at a fifth call site.
 */
const GATEWAY_PAYMENT_SOURCES: ReadonlySet<string> = new Set<string>([
  PaymentSource.Stripe,
  PaymentSource.StripeWalkIn,
  PaymentSource.StripeTerminal,
  PaymentSource.Swish,
  PaymentSource.SwishWalkIn,
  PaymentSource.Vipps,
  PaymentSource.Fritidskortet,
  PaymentSource.Epassi
])

export const isGatewayPayment = (source: string | undefined): boolean =>
  source != null && GATEWAY_PAYMENT_SOURCES.has(source)

const OPERATOR_TYPED_MANUAL_METHODS: ReadonlySet<string> = new Set<string>([
  PaymentMethod.SwishManual,
  PaymentMethod.VippsMobileManual
])

const OPERATOR_TYPED_MANUAL_SOURCES: ReadonlySet<string> = new Set<string>([
  PaymentSource.Swish,
  PaymentSource.Vipps,
  PaymentSource.Manual
])

const CAPTURED_WALK_IN_OR_TERMINAL_SOURCES: ReadonlySet<string> = new Set<string>([
  PaymentSource.SwishWalkIn,
  PaymentSource.StripeWalkIn,
  PaymentSource.StripeTerminal
])

/**
 * Whether an enrollment payment row may be deleted from the payments table.
 * Operator-typed Swish/Vipps Manual rows are stored with source swish/vipps
 * (legacy write mapping) and are deletable. Real Commerce charges and
 * walk-in / terminal captures are not.
 */
export const canDeletePayment = (payment: { source?: string; paymentMethod?: string }): boolean => {
  const source = payment.source
  const isOperatorTypedManual = OPERATOR_TYPED_MANUAL_METHODS.has(payment.paymentMethod || '')

  if (source != null && CAPTURED_WALK_IN_OR_TERMINAL_SOURCES.has(source)) {
    return false
  }

  if (isOperatorTypedManual && (source == null || source === '' || OPERATOR_TYPED_MANUAL_SOURCES.has(source))) {
    return true
  }

  return !isGatewayPayment(source)
}
